import type { InstallSourceDto } from "@intelligems/ig-types/dtos/organizations";

/**
 * Install channels that can be detected from UTM parameters.
 * These represent the different acquisition sources for app installs.
 */
export const InstallChannelList = [
  "shopify_app_store",
  "sales_channel",
  "partnership",
  "referral",
  "organic",
  "paid",
  "email",
  "social",
  "unknown",
] as const;

export type InstallChannel = (typeof InstallChannelList)[number];

/**
 * Human-readable labels for each install channel.
 */
export const InstallChannelLabels: Record<InstallChannel, string> = {
  shopify_app_store: "Shopify App Store",
  sales_channel: "Sales Channel",
  partnership: "Partnership",
  referral: "Referral",
  organic: "Organic",
  paid: "Paid",
  email: "Email",
  social: "Social",
  unknown: "Unknown",
};

/**
 * UTM parameters for install channel detection.
 * Accepts both null and undefined to support various usage patterns.
 */
export interface UtmParams {
  utmSource?: string | null;
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  utmContent?: string | null;
}

/**
 * Check if the parsed UTM params have any values.
 */
export function hasUtmParams(utm: UtmParams): boolean {
  return Boolean(
    utm.utmSource ||
    utm.utmMedium ||
    utm.utmCampaign ||
    utm.utmTerm ||
    utm.utmContent
  );
}

/**
 * Detect the install channel from UTM parameters.
 * Uses a priority-based detection system to categorize the install source.
 */
export function detectInstallChannel(
  utm: UtmParams | null | undefined
): InstallChannel {
  if (!utm) {
    return "unknown";
  }

  const source = utm.utmSource?.toLowerCase();
  const medium = utm.utmMedium?.toLowerCase();
  const campaign = utm.utmCampaign?.toLowerCase();

  // Sales channel detection (highest priority for internal leads)
  if (source === "sales" || medium === "sales") {
    return "sales_channel";
  }

  // Partnership detection
  if (
    source === "partner" ||
    source === "partnership" ||
    medium === "partner" ||
    medium === "partnership" ||
    campaign?.includes("partner")
  ) {
    return "partnership";
  }

  // Shopify App Store detection
  if (
    source === "shopify" ||
    source === "shopify_app_store" ||
    source === "app_store" ||
    medium === "shopify" ||
    medium === "app_store"
  ) {
    return "shopify_app_store";
  }

  // Referral detection
  if (
    source === "referral" ||
    source === "refer" ||
    medium === "referral" ||
    campaign?.includes("referral")
  ) {
    return "referral";
  }

  // Paid channel detection
  if (
    medium === "cpc" ||
    medium === "ppc" ||
    medium === "paid" ||
    medium === "display" ||
    source === "google" ||
    source === "facebook" ||
    source === "meta" ||
    source === "tiktok" ||
    source === "linkedin"
  ) {
    return "paid";
  }

  // Email detection
  if (medium === "email" || source === "email" || source === "newsletter") {
    return "email";
  }

  // Social detection
  if (
    medium === "social" ||
    source === "twitter" ||
    source === "instagram" ||
    source === "youtube"
  ) {
    return "social";
  }

  // Organic detection (has UTM params but doesn't match other channels)
  if (hasUtmParams(utm)) {
    return "organic";
  }

  return "unknown";
}

/**
 * Detect install channel from InstallSourceDto format.
 */
export function detectInstallChannelFromSource(
  installSource: InstallSourceDto | null | undefined
): InstallChannel {
  if (!installSource) {
    return "unknown";
  }

  return detectInstallChannel({
    utmSource: installSource.utmSource,
    utmMedium: installSource.utmMedium,
    utmCampaign: installSource.utmCampaign,
    utmTerm: installSource.utmTerm,
    utmContent: installSource.utmContent,
  });
}

/**
 * Get the human-readable label for an install channel.
 */
export function getInstallChannelLabel(channel: InstallChannel): string {
  return InstallChannelLabels[channel];
}
